const DEFAULT_MEASUREMENT_ID = "G-FKLS8LVX94";

const EVENT_PARAMETER_ALLOWLIST = Object.freeze({
  affiliate_click: ["affiliate_offer", "affiliate_audience", "placement", "site_language"],
  article_card_opened: [
    "article_slug",
    "article_category",
    "featured",
    "placement",
    "position",
    "site_language",
  ],
  cta_clicked: ["cta_id", "destination_type", "content_id", "placement", "site_language"],
  directory_filter_applied: ["filter_value", "result_count", "placement", "site_language"],
  internal_link_click: ["destination_path", "link_context", "placement", "site_language"],
  language_changed: ["from_language", "to_language", "page_type"],
  map_filter_applied: ["filter_value", "result_count", "placement", "site_language"],
  map_marker_selected: [
    "venue_slug",
    "venue_type",
    "venue_area",
    "placement",
    "position",
    "site_language",
  ],
  premium_booking_click: [
    "venue_slug",
    "venue_name",
    "venue_city",
    "venue_category",
    "venue_group",
    "contact_channel",
    "venue_partner",
    "placement",
    "site_language",
  ],
  premium_group_selected: ["filter_value", "result_count", "placement", "site_language"],
  venue_card_opened: [
    "venue_slug",
    "venue_type",
    "venue_area",
    "venue_tier",
    "placement",
    "position",
    "site_language",
  ],
  venue_contact_click: [
    "venue_slug",
    "venue_name",
    "venue_type",
    "venue_area",
    "contact_channel",
    "contact_tier",
    "venue_partner",
    "placement",
    "site_language",
  ],
});

const NUMERIC_EVENT_PARAMETERS = new Set(["position", "result_count"]);

export const ANALYTICS_CONSENT_STORAGE_KEY = "barspattaya.analytics-consent.v1";
export const ANALYTICS_CONSENT_EVENT = "barspattaya:analytics-consent-changed";
export const ANALYTICS_SETTINGS_EVENT = "barspattaya:open-analytics-settings";

export const GA_MEASUREMENT_ID = process.env.GATSBY_GA_ID || DEFAULT_MEASUREMENT_ID;

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";
let inMemoryConsent = null;

export function isAnalyticsRuntimeEnabled() {
  if (!GA_MEASUREMENT_ID || process.env.GATSBY_GA_ENABLED === "false") return false;

  // Never contaminate the live property while running `gatsby develop` unless
  // a developer explicitly opts in for DebugView testing.
  return process.env.NODE_ENV === "production" || process.env.GATSBY_GA_DEBUG === "true";
}

export function getAnalyticsConsent() {
  if (!isBrowser()) return null;

  try {
    const stored = window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY);
    if (!stored) return inMemoryConsent;

    const parsed = JSON.parse(stored);
    if (parsed?.choice === "granted" || parsed?.choice === "denied") {
      inMemoryConsent = parsed.choice;
      return parsed.choice;
    }
    return inMemoryConsent;
  } catch {
    return inMemoryConsent;
  }
}

function persistAnalyticsConsent(choice) {
  inMemoryConsent = choice;
  try {
    window.localStorage.setItem(
      ANALYTICS_CONSENT_STORAGE_KEY,
      JSON.stringify({ choice, version: 1, updatedAt: new Date().toISOString() }),
    );
  } catch {
    // Browsers can block localStorage. The in-memory choice still applies for
    // this page, but the banner will be shown again on the next visit.
  }
}

function setAnalyticsDisabled(disabled) {
  if (!isBrowser() || !GA_MEASUREMENT_ID) return;
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = disabled;
}

function removeAnalyticsCookies() {
  if (!isBrowser()) return;

  const host = window.location.hostname;
  const domainCandidates = ["", host, `.${host}`];
  const cookieNames = document.cookie
    .split(";")
    .map((cookie) => cookie.split("=")[0].trim())
    .filter((name) => name === "_ga" || name.startsWith("_ga_"));

  cookieNames.forEach((name) => {
    domainCandidates.forEach((domain) => {
      const domainPart = domain ? `; domain=${domain}` : "";
      document.cookie = `${name}=; Max-Age=0; path=/${domainPart}; SameSite=Lax`;
    });
  });
}

function initialiseDataLayer() {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };
}

function datasetKey(parameter) {
  const camelCase = parameter.replace(/_([a-z])/g, (_, character) => character.toUpperCase());
  return `analytics${camelCase.charAt(0).toUpperCase()}${camelCase.slice(1)}`;
}

function cleanEventParameters(name, params) {
  const allowed = EVENT_PARAMETER_ALLOWLIST[name];
  if (!allowed) return {};

  return Object.fromEntries(
    allowed.flatMap((key) => {
      const value = params[key];
      if (value === null || value === undefined || value === "") return [];

      if (NUMERIC_EVENT_PARAMETERS.has(key)) {
        const numeric = Number(value);
        return Number.isFinite(numeric) && numeric >= 0 ? [[key, numeric]] : [];
      }

      if (typeof value === "boolean") return [[key, value ? "yes" : "no"]];
      const text = String(value).trim().slice(0, 100);
      if (!text || text.includes("@")) return [];
      if (key === "destination_path") {
        const pathname = text.split(/[?#]/, 1)[0];
        return pathname.startsWith("/") ? [[key, pathname]] : [];
      }
      return [[key, text]];
    }),
  );
}

function inferLinkContext(pathname) {
  if (/\/(?:[a-z]{2}\/)?bars\/[^/]+\/?$/i.test(pathname)) return "venue";
  if (/\/(?:[a-z]{2}\/)?book\/[^/]+\/?$/i.test(pathname)) return "premium_venue";
  if (/\/(?:[a-z]{2}\/)?bars\/?$/i.test(pathname)) return "bar_directory";
  if (/\/(?:[a-z]{2}\/)?articles\/?$/i.test(pathname)) return "article_directory";
  if (/\/(?:[a-z]{2}\/)?map\/?$/i.test(pathname)) return "map";
  return "content";
}

function installInteractionTracking() {
  if (!isBrowser() || window.__BARSPATTAYA_INTERACTION_TRACKING__) return;

  document.addEventListener("click", (event) => {
    const element = event.target?.closest?.("[data-analytics-event],a[href]");
    if (!element) return;

    const explicitEvent = element.dataset?.analyticsEvent;
    if (explicitEvent && EVENT_PARAMETER_ALLOWLIST[explicitEvent]) {
      const params = Object.fromEntries(
        EVENT_PARAMETER_ALLOWLIST[explicitEvent].map((parameter) => [
          parameter,
          element.dataset[datasetKey(parameter)],
        ]),
      );
      trackAnalyticsEvent(explicitEvent, params);
      return;
    }

    if (element.tagName !== "A") return;
    const rawHref = element.getAttribute("href");
    if (!rawHref || /^(?:mailto:|tel:|javascript:)/i.test(rawHref)) return;

    let destination;
    try {
      destination = new URL(rawHref, window.location.href);
    } catch {
      return;
    }
    if (destination.origin !== window.location.origin) return;

    const placementElement = element.closest("[data-analytics-placement]");
    trackAnalyticsEvent("internal_link_click", {
      destination_path: destination.pathname,
      link_context: element.dataset?.analyticsContext || inferLinkContext(destination.pathname),
      placement: element.dataset?.analyticsPlacement || placementElement?.dataset?.analyticsPlacement || "content",
      site_language: document.documentElement.lang?.split("-")[0] || "en",
    });
  });

  window.__BARSPATTAYA_INTERACTION_TRACKING__ = true;
}

export function loadGoogleAnalytics() {
  if (!isAnalyticsRuntimeEnabled() || getAnalyticsConsent() !== "granted") return false;

  setAnalyticsDisabled(false);
  initialiseDataLayer();

  if (!window.__BARSPATTAYA_GA_CONFIGURED__) {
    // The tag is loaded only after a positive choice (Basic Consent Mode).
    // Ads-related storage and signals stay disabled because this integration
    // is for first-party site measurement, not ad personalisation.
    window.gtag("consent", "default", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    window.gtag("set", "ads_data_redaction", true);
    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      ...(process.env.GATSBY_GA_DEBUG === "true" ? { debug_mode: true } : {}),
    });
    window.__BARSPATTAYA_GA_CONFIGURED__ = true;
  }

  if (!document.getElementById("barspattaya-google-analytics")) {
    const script = document.createElement("script");
    script.id = "barspattaya-google-analytics";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
      GA_MEASUREMENT_ID,
    )}`;
    document.head.appendChild(script);
  }

  installInteractionTracking();

  return true;
}

export function setAnalyticsConsent(choice) {
  if (!isBrowser() || (choice !== "granted" && choice !== "denied")) return;

  const previousChoice = getAnalyticsConsent();
  persistAnalyticsConsent(choice);

  if (choice === "granted") {
    setAnalyticsDisabled(false);
    if (typeof window.gtag === "function" && window.__BARSPATTAYA_GA_CONFIGURED__) {
      window.gtag("consent", "update", {
        analytics_storage: "granted",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });
    }
    loadGoogleAnalytics();
  } else {
    setAnalyticsDisabled(true);
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });
    }
    removeAnalyticsCookies();
    window.__BARSPATTAYA_LAST_PAGE_VIEW__ = undefined;
  }

  window.dispatchEvent(
    new CustomEvent(ANALYTICS_CONSENT_EVENT, {
      detail: { choice, previousChoice },
    }),
  );
}

export function openAnalyticsSettings() {
  if (!isBrowser()) return;
  window.dispatchEvent(new CustomEvent(ANALYTICS_SETTINGS_EVENT));
}

function safePageLocation(location) {
  const pathname = location?.pathname || window.location.pathname || "/";
  return {
    pagePath: pathname,
    pageLocation: `${window.location.origin}${pathname}`,
  };
}

export function trackPageView(location) {
  if (!loadGoogleAnalytics()) return;

  const { pagePath, pageLocation } = safePageLocation(location);
  if (window.__BARSPATTAYA_LAST_PAGE_VIEW__ === pageLocation) return;

  window.__BARSPATTAYA_LAST_PAGE_VIEW__ = pageLocation;
  window.gtag("event", "page_view", {
    page_path: pagePath,
    page_location: pageLocation,
    page_title: document.title,
  });
}

export function initialiseAnalytics() {
  if (getAnalyticsConsent() === "granted") loadGoogleAnalytics();
}

export function trackAnalyticsEvent(name, params = {}) {
  if (!EVENT_PARAMETER_ALLOWLIST[name] || !loadGoogleAnalytics()) return;

  window.gtag("event", name, cleanEventParameters(name, params));
}
